import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check, MapPin, Clock, Users, ArrowRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT, Editable } from "../lib/i18n";
import { useSiteContent, useMedia } from "../lib/content";
import { formatApiError } from "../lib/AuthContext";
import { validatePhone } from "../lib/validate";
import TrialPromo from "../components/TrialPromo";
import { PhoneField } from "../components/PhoneField";
import { RevealLines, Rise, LineRise } from "../components/Reveal";

export default function Welkom() {
  const { lang, setLang, t } = useT();
  const { content } = useSiteContent();
  const media = useMedia();
  const trialSettings = content?.trial_settings || { enabled: true, date: "" };
  const { scrollY } = useScroll();
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.06]);
  const heroY = useTransform(scrollY, [0, 800], [0, 220]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.4]);

  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", experience: "" });
  const [loading, setLoading] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const pErr = validatePhone(form.phone, { required: true });
    if (pErr) { setPhoneErr(pErr); toast.error(pErr); return; }
    setLoading(true);
    try {
      await api.post("/forms/trial", form);
      toast.success(lang === "en" ? "Trial booked. Check your email." : "Proefles geboekt. Check je e-mail.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", experience: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "en" ? "Something went wrong" : "Er ging iets mis"));
    }
    setLoading(false);
  };

  const scrollToForm = () => document.getElementById("welkom-form")?.scrollIntoView({ behavior: "smooth" });
  const trialDateLabel = trialSettings.date
    ? new Date(trialSettings.date + "T00:00:00").toLocaleDateString(lang === "en" ? "en-GB" : "nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : null;

  const values = [
    { icon: <Check size={22} strokeWidth={1.5}/>, title: t("welkom.v1.t"), body: t("welkom.v1.b") },
    { icon: <Users size={22} strokeWidth={1.5}/>, title: t("welkom.v2.t"), body: t("welkom.v2.b") },
    { icon: <Clock size={22} strokeWidth={1.5}/>, title: t("welkom.v3.t"), body: t("welkom.v3.b") },
  ];
  const steps = [
    { n: "01 · 5 min", t: t("welkom.s1.t"), b: t("welkom.s1.b") },
    { n: "02 · 10 min", t: t("welkom.s2.t"), b: t("welkom.s2.b") },
    { n: "03 · 15 min", t: t("welkom.s3.t"), b: t("welkom.s3.b") },
    { n: "04 · 20 min", t: t("welkom.s4.t"), b: t("welkom.s4.b") },
    { n: "05 · 10 min", t: t("welkom.s5.t"), b: t("welkom.s5.b") },
  ];

  return (
    <main className="min-h-screen bg-[#F5F3F0] grain" data-testid="welkom-page">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 md:px-10 py-5 flex items-center justify-between backdrop-blur-md bg-[#F5F3F0]/70">
        <Link to="/" className="font-logo text-2xl md:text-3xl text-[#0A0A0A] accent-hover" data-testid="welkom-logo">JOH.</Link>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1 chip" data-testid="welkom-lang-toggle">
            <button onClick={() => setLang("nl")} className={lang === "nl" ? "text-[#0A0A0A]" : "text-[#9A968E] accent-hover"} data-testid="welkom-lang-nl">NL</button>
            <span className="text-[#9A968E]">/</span>
            <button onClick={() => setLang("en")} className={lang === "en" ? "text-[#0A0A0A]" : "text-[#9A968E] accent-hover"} data-testid="welkom-lang-en">EN</button>
          </div>
          <Link to="/" className="chip text-[#4A4A4A] accent-hover inline-flex items-center gap-2" data-testid="welkom-back-home">
            <ArrowLeft size={14}/> {t("welkom.back")}
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative min-h-[100svh] w-full overflow-hidden" data-testid="welkom-hero">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <video
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 30%" }}
            src={media("site.hero_video")}
            poster={media("site.hero_poster")}
            autoPlay
            loop
            muted
            playsInline
            data-testid="welkom-hero-video"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.3) 40%, rgba(10,10,10,0.9) 100%)" }}/>
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 pt-32 md:pt-36 px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }} className="section-tag section-tag-dark flex items-center gap-3">
            <span className="w-2 h-2 rounded-full" style={{ background: "var(--joh-accent)" }}/>
            {t("welkom.tag")}
          </motion.div>

          <h1 className="font-display uppercase text-[#F5F3F0] text-[13vw] md:text-[10.5vw] lg:text-[9vw] mt-10 leading-[0.88]" data-testid="welkom-title">
            <RevealLines
              lines={[
                <Editable k="welkom.h1.1">Do you</Editable>,
                <Editable k="welkom.h1.2">want to <span className="font-serif-italic normal-case tracking-tight accent-ink">learn</span></Editable>,
                <Editable k="welkom.h1.3">how to <span className="accent-ink">box</span>?</Editable>,
              ]}
              delay={0.55}
              lineDelay={0.16}
              duration={1.1}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-[#DDD6CB] text-lg md:text-2xl max-w-2xl leading-relaxed"
          >
            {t("welkom.intro")}
          </motion.p>

          <TrialPromo trialSettings={trialSettings} onCta={scrollToForm} />

          {/* Big CTA - inline under the intro paragraph */}
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 md:mt-7"
          >
            <button
              onClick={scrollToForm}
              className="group inline-flex items-center gap-6 md:gap-10 px-8 md:px-12 py-5 md:py-7 border-2 border-[#F5F3F0] bg-transparent text-[#F5F3F0] hover:bg-[#F5F3F0] hover:text-[#0A0A0A] transition-all duration-500"
              data-testid="welkom-cta-big"
            >
              <span className="font-display uppercase text-3xl md:text-5xl lg:text-6xl leading-none">
                <Editable k="welkom.cta">{lang === "en" ? "Let's get " : "Aan de "}<span className="font-serif-italic normal-case tracking-tight accent-ink">{lang === "en" ? "started" : "slag"}</span></Editable>
              </span>
              <ArrowRight size={36} strokeWidth={1.4} className="shrink-0 transition-transform duration-500 group-hover:translate-x-2"/>
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* Value strip */}
      <section className="line-b bg-[#F5F3F0] py-16 md:py-24">
        <div className="px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {values.map((v, i) => (
            <Rise key={i} delay={i * 0.08}>
              <div className="border border-[#0A0A0A] p-6 md:p-8 bg-[#EAE7E2] h-full" data-testid={`welkom-value-${i}`}>
                <div className="w-11 h-11 border border-[#0A0A0A] flex items-center justify-center accent-ink" style={{ color: "var(--joh-accent)" }}>{v.icon}</div>
                <h3 className="font-display uppercase text-2xl md:text-3xl mt-6 text-[#0A0A0A]">{v.title}</h3>
                <p className="mt-3 text-[#0A0A0A] leading-relaxed">{v.body}</p>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      {/* Photo showcase */}
      <section className="py-16 md:py-24 px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="lg:col-span-4">
            <div className="section-tag">— {t("welkom.gallery.tag")}</div>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.88] text-[#0A0A0A]" data-testid="welkom-gallery-title">
              <LineRise><Editable k="welkom.gallery.h1.1">{t("welkom.gallery.h1.1")}</Editable></LineRise>
              <LineRise delay={0.1}><span className="font-serif-italic normal-case tracking-tight text-[#4A4A4A]"><Editable k="welkom.gallery.h1.2">{t("welkom.gallery.h1.2")}</Editable></span></LineRise>
            </h2>
            <p className="mt-6 text-[#0A0A0A]">
              {t("welkom.gallery.p")}
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-12 gap-3 md:gap-4">
            <div className="col-span-7">
              <img src={media("welkom.gym")} alt="" className="w-full aspect-[4/3] object-cover" style={{ filter: "grayscale(15%) contrast(1.05)" }} data-testid="welkom-photo-gym"/>
            </div>
            <div className="col-span-5 flex flex-col gap-3 md:gap-4">
              <img src={media("welkom.coach")} alt="" className="w-full aspect-[4/5] object-cover" style={{ filter: "grayscale(15%) contrast(1.05)" }} data-testid="welkom-photo-coach"/>
              <img src={media("welkom.lineup")} alt="" className="w-full aspect-[4/3] object-cover" style={{ filter: "grayscale(15%) contrast(1.05)" }} data-testid="welkom-photo-lineup"/>
            </div>
            <div className="col-span-12">
              <img src={media("welkom.group")} alt="" className="w-full aspect-[16/6] object-cover" style={{ filter: "grayscale(15%) contrast(1.05)" }} data-testid="welkom-photo-group"/>
            </div>
          </div>
        </div>
      </section>

      {/* Session pattern */}
      <section className="bg-[#0A0A0A] text-[#F5F3F0] grain grain-dark py-20 md:py-28 px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-3 chip text-[#A3A3A3]">— {t("welkom.steps.tag")}</div>
          <h2 className="lg:col-span-9 font-display uppercase text-5xl md:text-7xl leading-[0.88]">
            <LineRise><Editable k="welkom.steps.h1.1">{t("welkom.steps.h1.1")}</Editable></LineRise>
            <LineRise delay={0.08}><span className="accent-ink font-serif-italic normal-case tracking-tight"><Editable k="welkom.steps.h1.2">{t("welkom.steps.h1.2")}</Editable></span></LineRise>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 line-t line-dark pt-8">
          {steps.map((s, i) => (
            <Rise key={i} delay={i * 0.06}>
              <div className="line-b line-dark md:line-r py-4 md:pr-6" data-testid={`welkom-step-${i}`}>
                <div className="chip accent-ink" style={{ color: "var(--joh-accent)" }}>{s.n}</div>
                <div className="font-display uppercase text-2xl md:text-3xl mt-2">{s.t}</div>
                <div className="chip text-[#A3A3A3] mt-2">{s.b}</div>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      {/* FORM */}
      <section id="welkom-form" className="py-20 md:py-28 px-6 md:px-10" data-testid="welkom-form-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="section-tag">— {t("welkom.form.tag")}</div>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.88] text-[#0A0A0A]">
              <LineRise><Editable k="welkom.form.h1.1">{t("welkom.form.h1.1")}</Editable></LineRise>
              <LineRise delay={0.08}><span className="accent-ink font-serif-italic normal-case tracking-tight"><Editable k="welkom.form.h1.2">{t("welkom.form.h1.2")}</Editable></span></LineRise>
            </h2>
            <div className="mt-8 space-y-4 text-[#0A0A0A]">
              <div className="flex items-start gap-3"><MapPin size={18} strokeWidth={1.5} className="mt-1"/> <a href="https://maps.google.com/?q=Rhoneweg+38+1043+AH+Amsterdam" target="_blank" rel="noreferrer" className="accent-hover">Rhoneweg 38, 1043 AH Amsterdam</a></div>
              <div className="flex items-start gap-3"><Clock size={18} strokeWidth={1.5} className="mt-1"/> {t("welkom.form.reply")}</div>
              <div className="flex items-start gap-3"><Users size={18} strokeWidth={1.5} className="mt-1"/> {trialDateLabel ? (lang === "en" ? `Trial date: ${trialDateLabel}` : `Proefles: ${trialDateLabel}`) : (lang === "en" ? "Choose any class from the schedule" : "Kies een les uit het rooster")}</div>
            </div>
          </div>

          {content && !trialSettings.enabled ? (
            <div className="lg:col-span-7 border border-[#0A0A0A] p-6 md:p-10 bg-[#EAE7E2] text-center" data-testid="welkom-trial-closed">
              <div className="chip text-[#4A4A4A] mb-4">{lang === "en" ? "Temporarily closed" : "Tijdelijk gesloten"}</div>
              <p className="text-[#0A0A0A] text-lg">{lang === "en"
                ? "Trial bookings are currently closed. Questions? Mail us at info@johboxing.com."
                : "Het inschrijven voor een proefles is momenteel gesloten. Vragen? Mail ons via info@johboxing.com."}</p>
            </div>
          ) : (
          <form onSubmit={submit} className="lg:col-span-7 border border-[#0A0A0A] p-6 md:p-10 bg-[#EAE7E2]" data-testid="welkom-trial-form">
            <div className="chip text-[#4A4A4A] mb-6">{t("welkom.form.badge")}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "First name*" : "Voornaam*"}</label>
                <input required value={form.first_name} onChange={set("first_name")} className="editorial-input" data-testid="welkom-first-name"/>
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Last name*" : "Achternaam*"}</label>
                <input required value={form.last_name} onChange={set("last_name")} className="editorial-input" data-testid="welkom-last-name"/>
              </div>
              <div>
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Email" : "E-mail"}</label>
                <input required type="email" value={form.email} onChange={set("email")} className="editorial-input" data-testid="welkom-email"/>
              </div>
              <div>
                <PhoneField value={form.phone} onChange={(v) => { setForm(f => ({ ...f, phone: v })); setPhoneErr(validatePhone(v, { required: true })); }} error={phoneErr} testid="welkom-phone" label={lang === "en" ? "Phone*" : "Telefoon*"}/>
              </div>
              <div className="md:col-span-2">
                <label className="chip text-[#4A4A4A]">{lang === "en" ? "Experience level*" : "Ervaringsniveau*"}</label>
                <select required value={form.experience} onChange={set("experience")} className="editorial-input" data-testid="welkom-experience">
                  <option value="" disabled>{lang === "en" ? "Choose your level" : "Kies je niveau"}</option>
                  <option value="beginner">Beginner</option>
                  <option value="gemiddeld">{lang === "en" ? "Intermediate" : "Gemiddeld"}</option>
                  <option value="gevorderd">{lang === "en" ? "Advanced" : "Gevorderd"}</option>
                </select>
              </div>
            </div>
            <button disabled={loading} type="submit" className="btn mt-8 w-full justify-center" data-testid="welkom-submit-btn">
              {loading ? (lang === "en" ? "Sending…" : "Versturen…") : (<>{t("welkom.form.submit")} <ArrowUpRight size={16}/></>)}
            </button>
            <p className="chip text-[#4A4A4A] mt-4 text-center">
              {trialDateLabel
                ? (lang === "en" ? `You are signing up for the trial on ${trialDateLabel}. We confirm within 24 hours.` : `Je schrijft je in voor de proefles op ${trialDateLabel}. Binnen 24 uur bevestigen we je plek.`)
                : (lang === "en" ? "We reply within 24 hours to confirm your class." : "Binnen 24 uur bevestigen we je les.")}
            </p>
          </form>
          )}
        </div>
      </section>
    </main>
  );
}
